import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiFolderPlus,
  FiLink,
  FiRefreshCw,
  FiSave,
  FiTrash2,
  FiX,
  FiSettings,
} from "react-icons/fi";
import api from "../api/http";
import { useLanguage } from "../context/LanguageContext";
import "./BoxSettings.css";

const TEXT_JA = {
  title: "Box連携設定",
  subtitle: "Boxの監視フォルダ、抽出テンプレート、出力ファイル形式を管理します。",
  loading: "読み込み中...",
  connected: "接続済み",
  disconnected: "未接続",
  connect: "Boxを連携する",
  reconnect: "Boxを再連携",
  repair: "フォルダ修復",
  createFolder: "フォルダ作成・連携",
  openLinkedFolder: "連携フォルダをBoxで開く",
  openCreatedFolder: "作成したフォルダをBoxで開く",
  connecting: "認証画面を開いています...",
  repairing: "修復中...",
  creatingFolder: "作成中...",
  save: "保存",
  connectHelp:
    "Boxにログインし、PITHIASのアクセスを許可するとOCR用フォルダが自動作成されます。",
  openingMessage:
    "Boxの認証画面を開いています。Boxにログイン、またはBoxアカウントを作成して、PITHIASへのアクセスを許可してください。",
  startError: "Box連携を開始できませんでした。",
  repairSuccess: "Boxフォルダを修復しました。",
  repairError: "Boxフォルダの修復に失敗しました。",
  createFolderSuccess: "連携フォルダに新しい抽出用フォルダを作成しました。",
  createFolderError: "Boxフォルダの作成・連携に失敗しました。",
  saveError: "Box設定の保存に失敗しました。",
  renameFolder: "名前変更",
  deleteFolder: "削除",
  cancel: "キャンセル",
  renameFolderSuccess: "Boxフォルダ名を変更しました。",
  renameFolderError: "Boxフォルダ名の変更に失敗しました。",
  deleteFolderSuccess: "Boxフォルダを削除し、連携設定から外しました。",
  deleteFolderError: "Boxフォルダの削除に失敗しました。",
  deleteFolderConfirm: "このBoxフォルダと連携設定を削除します。よろしいですか？",
  missingFolder: "Box側で削除された可能性があります。不要な場合は削除してください。",
  kicker: "連携設定",
  accountConnection: "Boxアカウント連携",
  connectionManagement: "接続管理",
  connectionManagementHelp:
    "連携中のBoxアカウントに合わせて、フォルダとWebhookを最新の状態に保ちます。",
  extractionDefaults: "抽出設定",
  extractionDefaultsHelp:
    "Boxフォルダにファイルがアップロードされたとき、この設定を使用して抽出します。",
  folderRouting: "フォルダ別の抽出設定",
  folderRoutingHelp:
    "Boxフォルダごとに使用するテンプレート、処理プラン、結果ファイル形式を選択します。",
  folderName: "フォルダ",
  templateSetting: "テンプレート",
  planSetting: "処理プラン",
  outputFileType: "結果ファイル形式",
  invoiceFolder: "Invoices",
  purchaseOrderFolder: "PurchaseOrders",
  receiptFolder: "Receipts",
  maskedPdfFolder: "Masked_PDF",
  customFolderName: "追加する抽出用フォルダ名",
  customFolderTemplate: "追加フォルダのテンプレート",
  customFolderPlan: "追加フォルダの処理プラン",
  customFolderOutput: "追加フォルダの結果形式",
  customFolderHelp:
    "連携済みのBox親フォルダ直下に作成し、選択した設定で自動抽出します。",
  selectTemplate: "テンプレートを選択",
  noTemplates:
    "保存済みテンプレートがありません。抽出画面でテンプレートを保存するとここに表示されます。",
  templateNameHelp:
    "抽出画面で保存したテンプレート名を選択します。Boxにファイルが入ると、ここで選んだテンプレートで抽出します。",
  backToExtraction: "抽出画面へ戻る",
  addonPaymentRequired: "BOX連携オプションのお支払いが必要です",
  addonPaymentDetails: "月1ユーザーあたり500円（税別）。請求書のご入金確認後にBox接続が有効になります。",
  applyWithBox: "プランとBOX連携を申し込む",
};

const TEXT_EN = {
  title: "Box integration settings",
  subtitle: "Manage monitored Box folders, extraction templates, and output formats.",
  loading: "Loading...", connected: "Connected", disconnected: "Not connected",
  connect: "Connect Box", reconnect: "Reconnect Box", repair: "Repair folders",
  createFolder: "Create and link folder", openLinkedFolder: "Open linked folder in Box",
  openCreatedFolder: "Open created folder in Box", connecting: "Opening authorization...",
  repairing: "Repairing...", creatingFolder: "Creating...", save: "Save",
  connectHelp: "Sign in to Box and grant PITHIAS access. OCR folders will be created automatically.",
  openingMessage: "The Box authorization page is opening. Sign in or create a Box account, then grant PITHIAS access.",
  startError: "Could not start Box integration.", repairSuccess: "Box folders were repaired.",
  repairError: "Could not repair the Box folders.",
  createFolderSuccess: "A new extraction folder was created in the linked folder.",
  createFolderError: "Could not create and link the Box folder.",
  saveError: "Could not save the Box settings.", renameFolder: "Rename", deleteFolder: "Delete",
  cancel: "Cancel", renameFolderSuccess: "The Box folder was renamed.",
  renameFolderError: "Could not rename the Box folder.",
  deleteFolderSuccess: "The Box folder and its integration settings were removed.",
  deleteFolderError: "Could not delete the Box folder.",
  deleteFolderConfirm: "Delete this Box folder and its integration settings?",
  missingFolder: "The folder may have been deleted in Box. Remove it here if it is no longer needed.",
  kicker: "INTEGRATION SETTINGS", accountConnection: "Box account integration",
  connectionManagement: "Connection management",
  connectionManagementHelp: "Keep folders and webhooks synchronized with the connected Box account.",
  extractionDefaults: "Extraction settings",
  extractionDefaultsHelp: "These settings are used when files are uploaded to a Box folder.",
  folderRouting: "Extraction settings by folder",
  folderRoutingHelp: "Choose the template, processing plan, and result format for each Box folder.",
  folderName: "Folder", templateSetting: "Template", planSetting: "Processing plan",
  outputFileType: "Result format", invoiceFolder: "Invoices", purchaseOrderFolder: "PurchaseOrders",
  receiptFolder: "Receipts", maskedPdfFolder: "Masked_PDF",
  customFolderName: "New extraction folder name", customFolderTemplate: "Template for new folder",
  customFolderPlan: "Plan for new folder", customFolderOutput: "Result format for new folder",
  customFolderHelp: "The folder will be created directly below the linked Box parent folder and processed using these settings.",
  selectTemplate: "Select a template",
  noTemplates: "No saved templates. Save a template on the extraction screen to use it here.",
  templateNameHelp: "Select a template saved on the extraction screen. Files added to Box will be extracted with it.",
  backToExtraction: "Back to extraction",
  addonPaymentRequired: "Payment for the Box integration add-on is required",
  addonPaymentDetails: "¥500 per user per month (before tax). Box connection becomes available after invoice payment is confirmed.",
  applyWithBox: "Apply for a plan with Box integration",
};

const PLAN_OPTIONS = ["Fast", "Standard", "Premium"];
const OUTPUT_OPTIONS = ["JSON", "CSV", "PDF"];

const BoxSettings = () => {
  const { isEnglish } = useLanguage();
  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const FIXED_FOLDER_ROUTES = [
    { key: "invoice", label: TEXT.invoiceFolder, templateField: "invoiceTemplateName" },
    { key: "purchaseOrder", label: TEXT.purchaseOrderFolder, templateField: "purchaseOrderTemplateName" },
    { key: "receipt", label: TEXT.receiptFolder, templateField: "receiptTemplateName" },
    { key: "maskedPdf", label: TEXT.maskedPdfFolder, templateField: "invoiceTemplateName" },
  ];
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [connected, setConnected] = useState(false);
  const [addonRequired, setAddonRequired] = useState(false);
  const [message, setMessage] = useState("");
  const [linkedRootFolderId, setLinkedRootFolderId] = useState(null);
  const [createdFolderUrl, setCreatedFolderUrl] = useState("");
  const [customFolderName, setCustomFolderName] = useState("");
  const [customFolderTemplateName, setCustomFolderTemplateName] = useState("");
  const [customFolderPlan, setCustomFolderPlan] = useState("Standard");
  const [customFolderOutputFileType, setCustomFolderOutputFileType] = useState("JSON");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [folderActionLoading, setFolderActionLoading] = useState("");

  const [form, setForm] = useState({
    invoiceTemplateName: "",
    purchaseOrderTemplateName: "",
    receiptTemplateName: "",
    defaultPlan: "Standard",
    outputFileType: "JSON",
    folderRoutes: [],
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setMessage("");
      setAddonRequired(false);

      const res = await api.get("/api/box/settings");
      const data = res.data?.data;

      if (res.data?.success && data) {
        setConnected(true);
        setLinkedRootFolderId(data.rootFolderId || null);
        setForm({
          invoiceTemplateName: data.invoiceTemplateName || "",
          purchaseOrderTemplateName: data.purchaseOrderTemplateName || "",
          receiptTemplateName: data.receiptTemplateName || "",
          defaultPlan: data.defaultPlan || "Standard",
          outputFileType: data.outputFileType || "JSON",
          folderRoutes: Array.isArray(data.folderRoutes) ? data.folderRoutes : [],
        });
        await loadTemplates();
      }
    } catch (err) {
      setConnected(false);
      setLinkedRootFolderId(null);
      if (err?.response?.status === 402 || err?.response?.data?.error === "BOX_ADDON_REQUIRED") {
        setAddonRequired(true);
        setMessage(err?.response?.data?.message || "A paid Box integration add-on is required.");
      } else {
        setMessage("");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const res = await api.get("/api/v1/templates");
      const list = res.data?.data?.templates || [];
      setTemplates(Array.isArray(list) ? list : []);
    } catch (err) {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const persistSettings = async (nextForm) => {
    try {
      setMessage("");
      const res = await api.put("/api/box/settings", nextForm);
      if (!res.data?.success) {
        setMessage(res.data?.error || TEXT.saveError);
      }
    } catch (err) {
      setMessage(err.response?.data?.error || TEXT.saveError);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const nextForm = { ...prev, [name]: value };
      void persistSettings(nextForm);
      return nextForm;
    });
  };

  const handleCustomRouteChange = (folderId, field, value) => {
    setForm((prev) => {
      const nextForm = {
        ...prev,
        folderRoutes: (prev.folderRoutes || []).map((route) =>
          route.folderId === folderId ? { ...route, [field]: value } : route
        ),
      };
      void persistSettings(nextForm);
      return nextForm;
    });
  };

  const handleStartRenameFolder = (route) => {
    setEditingFolderId(route.folderId);
    setEditingFolderName(route.folderName || "");
    setMessage("");
  };

  const handleCancelRenameFolder = () => {
    setEditingFolderId(null);
    setEditingFolderName("");
  };

  const handleRenameFolder = async (folderId) => {
    const nextName = editingFolderName.trim();
    if (!nextName) return;

    try {
      setFolderActionLoading(folderId);
      setMessage("");
      const res = await api.patch("/api/box/settings/folders", {
        folderId,
        folderName: nextName,
      });

      if (res.data?.success) {
        setForm((prev) => ({
          ...prev,
          folderRoutes: (prev.folderRoutes || []).map((route) =>
            route.folderId === folderId
              ? { ...route, folderName: nextName, missing: false }
              : route
          ),
        }));
        handleCancelRenameFolder();
        setMessage(TEXT.renameFolderSuccess);
      } else {
        setMessage(res.data?.error || TEXT.renameFolderError);
      }
    } catch (err) {
      setMessage(err.response?.data?.error || TEXT.renameFolderError);
    } finally {
      setFolderActionLoading("");
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm(TEXT.deleteFolderConfirm)) return;

    try {
      setFolderActionLoading(folderId);
      setMessage("");
      const res = await api.delete("/api/box/settings/folders", {
        data: { folderId },
      });

      if (res.data?.success) {
        setForm((prev) => ({
          ...prev,
          folderRoutes: (prev.folderRoutes || []).filter(
            (route) => route.folderId !== folderId
          ),
        }));
        setMessage(TEXT.deleteFolderSuccess);
      } else {
        setMessage(res.data?.error || TEXT.deleteFolderError);
      }
    } catch (err) {
      setMessage(err.response?.data?.error || TEXT.deleteFolderError);
    } finally {
      setFolderActionLoading("");
    }
  };
  const handleConnectBox = async () => {
    try {
      setConnecting(true);
      setMessage(TEXT.openingMessage);

      const res = await api.get("/api/box/oauth/start?format=json", {
        withCredentials: true,
      });

      const boxOAuthUrl = res.data?.url;

      if (!res.data?.success || !boxOAuthUrl) {
        setMessage(res.data?.error || TEXT.startError);
        setConnecting(false);
        return;
      }

      window.location.assign(boxOAuthUrl);
    } catch (err) {
      setMessage(err.response?.data?.error || TEXT.startError);
      setConnecting(false);
    }
  };

  const handleRepairFolders = async () => {
    try {
      setRepairing(true);
      setMessage("");

      const res = await api.post("/api/box/settings/repair");

      if (res.data?.success) {
        setMessage(res.data?.message || TEXT.repairSuccess);
        await loadSettings();
      } else {
        setMessage(res.data?.error || TEXT.repairError);
      }
    } catch (err) {
      setMessage(err.response?.data?.error || TEXT.repairError);
    } finally {
      setRepairing(false);
    }
  };

  const handleCreateSubFolder = async () => {
    try {
      setCreatingFolder(true);
      setMessage("");

      const res = await api.post("/api/box/settings/folders", {
        folderName: customFolderName,
        templateName: customFolderTemplateName,
        defaultPlan: customFolderPlan,
        outputFileType: customFolderOutputFileType,
      });

      if (res.data?.success) {
        setCreatedFolderUrl(res.data?.data?.boxUrl || "");
        setCustomFolderName("");
        setCustomFolderTemplateName("");
        setCustomFolderPlan("Standard");
        setCustomFolderOutputFileType("JSON");
        await loadSettings();
        setMessage(TEXT.createFolderSuccess);
      } else {
        setMessage(res.data?.error || TEXT.createFolderError);
      }
    } catch (err) {
      setMessage(err.response?.data?.error || TEXT.createFolderError);
    } finally {
      setCreatingFolder(false);
    }
  };

  const renderTemplateOptions = () => (
    <>
      <option value="">{templatesLoading ? TEXT.loading : TEXT.selectTemplate}</option>
      {templates.map((template) => (
        <option key={template.name} value={template.name}>
          {template.name}
          {template.created_with_plan ? ` (${template.created_with_plan})` : ""}
          {template.has_mask ? " 🛡️" : ""}
        </option>
      ))}
    </>
  );

  const renderPlanOptions = () =>
    PLAN_OPTIONS.map((plan) => (
      <option key={plan} value={plan}>
        {plan}
      </option>
    ));

  const renderOutputOptions = () =>
    OUTPUT_OPTIONS.map((type) => (
      <option key={type} value={type}>
        {type}
      </option>
    ));

  return (
    <section className="box-settings-page">
      <div className="box-settings-shell">
        <Link to="/ocr-extract-view" className="box-back-button">
          <FiArrowLeft aria-hidden="true" />
          {TEXT.backToExtraction}
        </Link>
        <header className="box-settings-header">
          <div>
            <p className="box-settings-kicker">{TEXT.kicker}</p>
            <h2>{TEXT.title}</h2>
            <p>{TEXT.subtitle}</p>
          </div>
          <div className="box-header-actions">
            <div
              className={`box-status-chip ${
                connected ? "box-status-chip--connected" : "box-status-chip--off"
              }`}
            >
              <FiCheckCircle aria-hidden="true" />
              <span>{connected ? TEXT.connected : TEXT.disconnected}</span>
            </div>
          </div>
        </header>

        {addonRequired && !loading && (
          <div className="box-settings-panel box-connect-panel" style={{ borderColor: "#5068e8" }}>
            <div className="box-panel-title">
              <FiLink aria-hidden="true" />
              <div>
                <h3>{TEXT.addonPaymentRequired}</h3>
                <p>{TEXT.addonPaymentDetails}</p>
              </div>
            </div>
            <Link to="/settings/invoice?boxAddon=true" className="box-primary-button">
              {TEXT.applyWithBox}
            </Link>
          </div>
        )}

        {loading ? (
          <div className="box-settings-panel">
            <p className="box-settings-loading">{TEXT.loading}</p>
          </div>
        ) : addonRequired ? null : !connected ? (
          <div className="box-settings-panel box-connect-panel">
            <div className="box-panel-title">
              <FiLink aria-hidden="true" />
              <div>
                <h3>{TEXT.accountConnection}</h3>
                <p>{TEXT.connectHelp}</p>
              </div>
            </div>
            <button
              type="button"
              className="box-primary-button"
              onClick={handleConnectBox}
              disabled={connecting}
            >
              <FiLink aria-hidden="true" />
              {connecting ? TEXT.connecting : TEXT.connect}
            </button>
          </div>
        ) : (
          <div className="box-settings-grid">
            <div className="box-settings-panel box-settings-panel--compact">
              <div className="box-panel-title">
                <FiSettings aria-hidden="true" />
                <div>
                  <h3>{TEXT.connectionManagement}</h3>
                  <p>{TEXT.connectionManagementHelp}</p>
                </div>
              </div>

              <div className="box-action-row">
                <button
                  type="button"
                  className="box-secondary-button"
                  onClick={handleRepairFolders}
                  disabled={repairing}
                >
                  <FiRefreshCw aria-hidden="true" />
                  {repairing ? TEXT.repairing : TEXT.repair}
                </button>

                <button
                  type="button"
                  className="box-secondary-button"
                  onClick={handleConnectBox}
                  disabled={connecting}
                >
                  <FiLink aria-hidden="true" />
                  {connecting ? TEXT.connecting : TEXT.reconnect}
                </button>

                {linkedRootFolderId && (
                  <a
                    className="box-secondary-button"
                    href={`https://app.box.com/folder/${linkedRootFolderId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FiExternalLink aria-hidden="true" />
                    {TEXT.openLinkedFolder}
                  </a>
                )}
              </div>
            </div>

            <div className="box-settings-panel">
              <div className="box-panel-title">
                <FiFileText aria-hidden="true" />
                <div>
                  <h3>{TEXT.extractionDefaults}</h3>
                  <p>{TEXT.extractionDefaultsHelp}</p>
                </div>
              </div>

              <div className="box-routing-summary">
                <div>
                  <h4>{TEXT.folderRouting}</h4>
                  <p>{TEXT.folderRoutingHelp}</p>
                </div>

                <div className="box-route-list">
                  {FIXED_FOLDER_ROUTES.map((route) => (
                    <div className="box-route-card" key={route.key}>
                      <div className="box-route-name">{route.label}</div>
                      <label className="box-field">
                        <span>{TEXT.templateSetting}</span>
                        <select
                          name={route.templateField}
                          value={form[route.templateField]}
                          onChange={handleChange}
                          disabled={templatesLoading || templates.length === 0}
                        >
                          {renderTemplateOptions()}
                        </select>
                      </label>
                      <label className="box-field">
                        <span>{TEXT.planSetting}</span>
                        <select
                          name="defaultPlan"
                          value={form.defaultPlan}
                          onChange={handleChange}
                        >
                          {renderPlanOptions()}
                        </select>
                      </label>
                      <label className="box-field">
                        <span>{TEXT.outputFileType}</span>
                        <select
                          name="outputFileType"
                          value={form.outputFileType}
                          onChange={handleChange}
                        >
                          {renderOutputOptions()}
                        </select>
                      </label>
                    </div>
                  ))}

                  {(form.folderRoutes || []).map((route) => (
                    <div
                      className={`box-route-card box-route-card--custom ${
                        route.missing ? "box-route-card--missing" : ""
                      }`}
                      key={route.folderId}
                    >
                      <div className="box-route-name-group">
                        {editingFolderId === route.folderId ? (
                          <div className="box-route-edit">
                            <input
                              type="text"
                              value={editingFolderName}
                              onChange={(e) => setEditingFolderName(e.target.value)}
                              maxLength={255}
                              disabled={folderActionLoading === route.folderId}
                            />
                            <div className="box-route-actions">
                              <button
                                type="button"
                                className="box-inline-button"
                                onClick={() => handleRenameFolder(route.folderId)}
                                disabled={
                                  folderActionLoading === route.folderId ||
                                  !editingFolderName.trim()
                                }
                              >
                                <FiSave aria-hidden="true" />
                                {TEXT.save}
                              </button>
                              <button
                                type="button"
                                className="box-inline-button"
                                onClick={handleCancelRenameFolder}
                                disabled={folderActionLoading === route.folderId}
                              >
                                <FiX aria-hidden="true" />
                                {TEXT.cancel}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="box-route-name">{route.folderName}</div>
                            {route.missing && (
                              <p className="box-route-missing">{TEXT.missingFolder}</p>
                            )}
                            <div className="box-route-actions">
                              <button
                                type="button"
                                className="box-inline-button"
                                onClick={() => handleStartRenameFolder(route)}
                                disabled={folderActionLoading === route.folderId}
                              >
                                <FiEdit2 aria-hidden="true" />
                                {TEXT.renameFolder}
                              </button>
                              <button
                                type="button"
                                className="box-inline-button box-inline-button--danger"
                                onClick={() => handleDeleteFolder(route.folderId)}
                                disabled={folderActionLoading === route.folderId}
                              >
                                <FiTrash2 aria-hidden="true" />
                                {TEXT.deleteFolder}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                      <label className="box-field">
                        <span>{TEXT.templateSetting}</span>
                        <select
                          value={route.templateName || ""}
                          onChange={(e) =>
                            handleCustomRouteChange(
                              route.folderId,
                              "templateName",
                              e.target.value
                            )
                          }
                          disabled={templatesLoading || templates.length === 0}
                        >
                          {renderTemplateOptions()}
                        </select>
                      </label>
                      <label className="box-field">
                        <span>{TEXT.planSetting}</span>
                        <select
                          value={route.defaultPlan || form.defaultPlan}
                          onChange={(e) =>
                            handleCustomRouteChange(
                              route.folderId,
                              "defaultPlan",
                              e.target.value
                            )
                          }
                        >
                          {renderPlanOptions()}
                        </select>
                      </label>
                      <label className="box-field">
                        <span>{TEXT.outputFileType}</span>
                        <select
                          value={route.outputFileType || form.outputFileType}
                          onChange={(e) =>
                            handleCustomRouteChange(
                              route.folderId,
                              "outputFileType",
                              e.target.value
                            )
                          }
                        >
                          {renderOutputOptions()}
                        </select>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="box-custom-folder-panel">
                <h4>{TEXT.createFolder}</h4>
                <p>{TEXT.customFolderHelp}</p>
                <div className="box-custom-folder-grid">
                  <label className="box-field">
                    <span>{TEXT.customFolderName}</span>
                    <input
                      type="text"
                      value={customFolderName}
                      onChange={(e) => setCustomFolderName(e.target.value)}
                      maxLength={255}
                      placeholder={"\u4f8b\uff1a\u53d6\u5f15\u5148\u5225\u4fdd\u7ba1"}
                    />
                  </label>
                  <label className="box-field">
                    <span>{TEXT.customFolderTemplate}</span>
                    <select
                      value={customFolderTemplateName}
                      onChange={(e) => setCustomFolderTemplateName(e.target.value)}
                      disabled={templatesLoading || templates.length === 0}
                    >
                      {renderTemplateOptions()}
                    </select>
                  </label>
                  <label className="box-field">
                    <span>{TEXT.customFolderPlan}</span>
                    <select
                      value={customFolderPlan}
                      onChange={(e) => setCustomFolderPlan(e.target.value)}
                    >
                      {renderPlanOptions()}
                    </select>
                  </label>
                  <label className="box-field">
                    <span>{TEXT.customFolderOutput}</span>
                    <select
                      value={customFolderOutputFileType}
                      onChange={(e) => setCustomFolderOutputFileType(e.target.value)}
                    >
                      {renderOutputOptions()}
                    </select>
                  </label>
                </div>
                <div className="box-action-row">
                  <button
                    type="button"
                    className="box-primary-button"
                    onClick={handleCreateSubFolder}
                    disabled={
                      creatingFolder ||
                      !customFolderName.trim() ||
                      !customFolderTemplateName
                    }
                  >
                    <FiFolderPlus aria-hidden="true" />
                    {creatingFolder ? TEXT.creatingFolder : TEXT.createFolder}
                  </button>
                  {createdFolderUrl && (
                    <a
                      className="box-secondary-button"
                      href={createdFolderUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FiExternalLink aria-hidden="true" />
                      {TEXT.openCreatedFolder}
                    </a>
                  )}
                </div>
              </div>

              <p className="box-template-note">
                {templates.length === 0 && !templatesLoading
                  ? TEXT.noTemplates
                  : TEXT.templateNameHelp}
              </p>


            </div>
          </div>
        )}

        {message && <p className="box-settings-message">{message}</p>}
      </div>
    </section>
  );
};

export default BoxSettings;
